/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Link as ScrollLink } from 'react-scroll';
import {
  CircularProgress,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { CheckCircle, ExpandLess, ExpandMore } from '@material-ui/icons';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import clsx from 'clsx';
import { useLocation } from 'react-use';
import { useTranslation } from 'react-i18next';
import { scrollerSettings } from '../../settings';
import Tooltip from '../common/Tooltip';
import useScrollLinkItem from './useScrollLinkItems';
import Button from '../common/Button';

const useStyles = makeStyles(theme => ({
  ...theme.customTheme,
  tooltipItem: {
    marginBottom: theme.spacing(1),
  },
  paragraph: {
    paddingBottom: theme.spacing(2),
  },
  itemText: {
    overflowWrap: 'break-word',
  },
  listItemSecondaryAction: {
    marginTop: 3,
    right: '2rem',
    padding: 0,
  },
  completedIcon: {
    opacity: 0,
    position: 'absolute',
    left: 0,
    color: theme.palette.completed,
    transition: 'opacity 0.5s ease-in',
    transitionDelay: '0.0s',
  },
  completedIconVisible: {
    opacity: 100,
    transitionDelay: '0.5s',
    textAlign: 'center',
    position: 'absolute',
    left: 0,
  },
  completedProgress: {
    color: theme.palette.completed,
  },
  inProgressBackground: {
    color: theme.palette.white,
    position: 'absolute',
    left: 0,
  },
  completedProgressBackground: {
    color: theme.palette.completedProgressBackground,
    position: 'absolute',
    left: 0,
  },
  completedProgressBackgroundActive: {
    color: theme.palette.completedProgressBackgroundActive,
    position: 'absolute',
    left: 0,
  },
  inProgressBackgroundActive: {
    color: theme.palette.inProgressBackgroundActive,
    position: 'absolute',
    left: 0,
  },
  '@global': {
    '.filledIconButton .MuiButton-iconSizeMedium': {
      marginLeft: 0,
      marginRight: 0,
    },
  },
  scrollLink: {
    position: 'relative',
    display: 'flex',
    flexGrow: '1',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    width: 'calc(100% + 4rem)',
    marginLeft: '-2rem',
    marginRight: '-2rem',
    paddingLeft: '2rem',
    paddingRight: '2rem',
    color: 'inherit',
    textDecoration: 'none',
    '&:hover': {
      outline: '0',
      background: theme.palette.sideMenuHoverBackground,
      color: theme.palette.sideMenuHoverText,
    },
    '&[data-focustype="key"]': {
      color: theme.palette.sideMenuHoverText,
      boxShadow: `0 0 0 2px ${theme.palette.sidebarFocusBorder}, 0 0 0 5px ${theme.palette.sidebarFocus}`,
      outline: 'none',
      width: 'calc(100% + 3.4rem)',
      marginLeft: '-1.65rem',
      paddingLeft: '1.8rem',
      paddingRight: '1.8rem',
      '&:before': {
        borderLeft: 'none !important',
      },
    },
  },
  scrollItem: {
    color: theme.palette.sideMenuText,
    background: 'transparent',
    width: 'calc(100% - 4rem)',
    margin: '0 2rem',
    padding: '0',

    '&[data-focustype="key"]': {
      color: theme.palette.sideMenuHoverText,
      boxShadow: `0 0 0 2px ${theme.palette.sidebarFocusBorder}, 0 0 0 5px ${theme.palette.sidebarFocus}`,
      outline: 'none',
      width: 'calc(100% + 3.4rem)',
      marginLeft: '-1.65rem',
      '&:before': {
        borderLeft: 'none !important',
      },
    },
  },
  sideMenuIcon: {
    color: 'inherit',
    alignItems: 'center',
  },
  active: {
    '& .MuiListItemText-primary': {
      fontWeight: 'bold',
    },

    backgroundColor: theme.palette.white,
    color: theme.palette.sideMenuHoverText,
    '&:before': {
      content: '""',
      position: 'absolute',
      height: '3rem',
      left: 0,
      top: 0,
      borderLeft: `.25rem solid ${theme.palette.sidebarHighlightBackground}`,
    },
  },
  dropdownButton: {
    position: 'absolute',
    right: '-3rem',
    top: 5,
  },
}));

function ScrollLinkItemProgress({ id, progress, isActive }) {
  const classes = useStyles();
  const { t } = useTranslation();
  if (progress == null) return <></>;

  return (
    <div id={`scrollSection_${id}`} data-progress={progress}>
      {isActive ? (
        <CircularProgress
          variant="determinate"
          thickness={6}
          value={100}
          size={24}
          edge="end"
          className={clsx(progress ? classes.inProgressBackgroundActive : classes.completedProgressBackgroundActive)}
          aria-label={t('section.progress')}
        />
      ) : (
        <CircularProgress
          variant="determinate"
          thickness={6}
          value={100}
          size={24}
          edge="end"
          className={clsx(progress ? classes.inProgressBackground : classes.completedProgressBackground)}
          aria-label={t('section.progress')}
        />
      )}
      <CircularProgress
        variant="determinate"
        thickness={6}
        size={24}
        edge="end"
        value={progress}
        className={classes.completedProgress}
        aria-label={t('section.progress')}
      />
      <FiberManualRecordIcon
        style={{
          color: 'white',
          zIndex: '-1',
        }}
        className={clsx(classes.completedIcon, progress === 100 ? classes.completedIconVisible : null)}
      />
      <CheckCircle
        className={clsx(classes.completedIcon, progress === 100 ? classes.completedIconVisible : null)}
        aria-label={t('section.progress')}
      />
    </div>
  );
}

export function ScrollLinkItem({
  to,
  label,
  icon,
  progress,
  description,
  offset,
  hasDropdown,
  toggleDropdown,
  isSummaryVisible,
}) {
  const classes = useStyles();
  const location = useLocation();
  const { t } = useTranslation();
  const { handleSetActive, active } = useScrollLinkItem(to);

  const item = (
    <ListItem className={classes.scrollItem}>
      <ScrollLink
        tabIndex={0}
        onSetActive={handleSetActive}
        onSetInactive={handleSetActive}
        activeClass={classes.active}
        to={to}
        href={`${location.pathname}#${to}`}
        smooth={scrollerSettings.smooth}
        offset={offset || scrollerSettings.offset}
        delay={scrollerSettings.delay}
        duration={scrollerSettings.duration}
        spy={true}
        className={classes.scrollLink}
      >
        <ListItemIcon className={classes.sideMenuIcon}>{icon}</ListItemIcon>
        <ListItemText className={classes.itemText} id={`${to}_text`}>
          <Typography variant="body1">
            <span className={classes.visuallyHidden}>{t('scroll.to')}</span> 
            {' '}
            {label}
          </Typography>
        </ListItemText>
        <ListItemSecondaryAction className={classes.listItemSecondaryAction}>
          <ScrollLinkItemProgress id={`${to}_progress`} progress={progress} isActive={active} />
        </ListItemSecondaryAction>
      </ScrollLink>
      {hasDropdown && (
        <Button
          className={classes.dropdownButton}
          variant="text"
          id="language"
          size="small"
          color="inherit"
          onClick={toggleDropdown}
          aria-controls="simple-menu"
        >
          {isSummaryVisible ? <ExpandMore /> : <ExpandLess />}
        </Button>
      )}
    </ListItem>
  );
  return (
    <>
      {description && description.length ? (
        <Tooltip
          className={classes.tooltip}
          title={description.map(desc => (
            <div key={desc} className={classes.tooltipItem}>
              {desc}
            </div>
          ))}
          placement="left"
        >
          {item}
        </Tooltip>
      ) : (
        item
      )}
    </>
  );
}
